"use client"

import * as React from "react"
import { cn } from "cn"

// 좁은 화면(640px 미만)에서 표는 줄이 된다 — 머리가 접히고(열 폭이 머리에만 있어 함께 사라진다)
// 칸이 흐르며 줄바꿈 금지가 풀린다. 표마다 따로 적지 않는다.
// 칸 규칙은 후손 선택자라 TableCell 자신의 whitespace-nowrap·p-2·정렬을 순서와 무관하게 이긴다.
//
// 글자 크기는 얹지 않는다 — 생성물의 text-sm(14px)은 이 저장소의 기본(theme.css 의 body, 13px)보다
// 한 단계 커서 표가 있는 화면만 튄다. 크기의 원본은 body 하나다.
//
// 생성물을 다시 받으면 이 상수와 STACK 을 끼우는 두 자리, 그리고 Table·TableCaption 에서 뺀
// text-sm 을 되살린다.
const STACK =
  "max-sm:block [&_thead]:max-sm:hidden [&_tbody]:max-sm:block " +
  "[&_tr]:max-sm:flex [&_tr]:max-sm:flex-wrap [&_tr]:max-sm:items-baseline " +
  "[&_tr]:max-sm:gap-x-3 [&_tr]:max-sm:gap-y-1 [&_tr]:max-sm:px-3 [&_tr]:max-sm:py-2.5 " +
  "[&_td]:max-sm:w-auto [&_td]:max-sm:whitespace-normal [&_td]:max-sm:p-0 [&_td]:max-sm:text-left"

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto overscroll-x-contain"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom", STACK, className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b transition-colors hover:bg-muted/50 has-aria-expanded:bg-muted/50 data-[state=selected]:bg-muted",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
